// Cases.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { UserContext } from './App';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBoxes, FaBoxOpen, FaSpinner, FaHistory, FaCoins,
  FaCheck, FaCopy, FaGift, FaFireAlt, FaStar, FaGem
} from 'react-icons/fa';
import './Dashboard.css'; // Same CSS as dashboard

// Case Opening Animation Component (same as in Dashboard)
const CaseOpeningAnimation = ({ isOpen, onClose, caseType, onOpenCase }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [finalReward, setFinalReward] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [rollItems, setRollItems] = useState([]);
  const [rollDistance, setRollDistance] = useState(0);
  
  // Sound effects
  const openingSoundRef = useRef(new Audio('/sounds/case-opening.mp3'));
  const rollingSoundRef = useRef(new Audio('/sounds/rolling.mp3'));
  const winSoundRef = useRef(new Audio('/sounds/win.mp3'));
  
  useEffect(() => {
    // Set volumes
    if (openingSoundRef.current) openingSoundRef.current.volume = 0.6;
    if (rollingSoundRef.current) {
      rollingSoundRef.current.volume = 0.4;
      rollingSoundRef.current.loop = true;
    }
    if (winSoundRef.current) winSoundRef.current.volume = 0.7;
    
    // Clean up sounds
    return () => {
      if (openingSoundRef.current) {
        openingSoundRef.current.pause();
        openingSoundRef.current.currentTime = 0;
      }
      if (rollingSoundRef.current) {
        rollingSoundRef.current.pause();
        rollingSoundRef.current.currentTime = 0;
      }
      if (winSoundRef.current) {
        winSoundRef.current.pause();
        winSoundRef.current.currentTime = 0;
      }
    };
  }, []);

  // Define possible rewards based on case rarity
  const caseRewards = {
    common: [
      { name: 'Bronze Coin', value: 0.10, rarity: 'Common', emoji: '🥉', color: '#cd7f32' },
      { name: 'Silver Coin', value: 0.25, rarity: 'Uncommon', emoji: '🥈', color: '#c0c0c0' },
      { name: 'Gold Coin', value: 0.50, rarity: 'Rare', emoji: '🥇', color: '#ffd700' },
      { name: 'Diamond', value: 1.00, rarity: 'Epic', emoji: '💎', color: '#ffb703' },
      { name: 'Ruby', value: 2.00, rarity: 'Legendary', emoji: '💍', color: '#ff6b35' }
    ],
    rare: [
      { name: 'Silver Coin', value: 0.50, rarity: 'Common', emoji: '🥈', color: '#c0c0c0' },
      { name: 'Gold Coin', value: 1.00, rarity: 'Uncommon', emoji: '🥇', color: '#ffd700' },
      { name: 'Diamond', value: 2.50, rarity: 'Rare', emoji: '💎', color: '#ffb703' },
      { name: 'Ruby', value: 5.00, rarity: 'Epic', emoji: '💍', color: '#ff6b35' },
      { name: 'Crown', value: 10.00, rarity: 'Legendary', emoji: '👑', color: '#ffb703' }
    ],
    epic: [
      { name: 'Gold Coin', value: 2.00, rarity: 'Common', emoji: '🥇', color: '#ffd700' },
      { name: 'Diamond', value: 5.00, rarity: 'Uncommon', emoji: '💎', color: '#ffb703' },
      { name: 'Ruby', value: 10.00, rarity: 'Rare', emoji: '💍', color: '#ff6b35' },
      { name: 'Crown', value: 25.00, rarity: 'Epic', emoji: '👑', color: '#ffb703' },
      { name: 'Star', value: 50.00, rarity: 'Legendary', emoji: '⭐', color: '#ff3b30' }
    ]
  };

  const rewards = caseRewards[caseType] || caseRewards.common;

  // Generate fake items for rolling animation with precise positioning
  const generateRollItems = (targetReward) => {
    const items = [];
    const totalItems = 50;
    const targetIndex = 25;
    
    for (let i = 0; i < targetIndex; i++) {
      const randomReward = rewards[Math.floor(Math.random() * rewards.length)];
      items.push({ ...randomReward, id: `fake-${i}`, index: i });
    }
    
    items.push({ ...targetReward, id: 'target', index: targetIndex });
    
    for (let i = targetIndex + 1; i < totalItems; i++) {
      const randomReward = rewards[Math.floor(Math.random() * rewards.length)];
      items.push({ ...randomReward, id: `fake-${i}`, index: i });
    }
    
    return items;
  };
  
  const calculateRollDistance = (targetIndex) => {
    const itemWidth = 120;
    const containerWidth = 500;
    const centerOffset = containerWidth / 2;
    const targetPosition = targetIndex * itemWidth;
    
    return -(targetPosition - centerOffset + itemWidth / 2) + 60;
  };

  const handleOpenCase = async () => {
    setIsOpening(true);
    setIsRolling(false);
    setShowResult(false);
    setFinalReward(null);

    try {
      if (openingSoundRef.current) {
        openingSoundRef.current.currentTime = 0;
        openingSoundRef.current.play().catch(err => console.log('Could not play opening sound:', err));
      }

      const result = await onOpenCase(caseType);
      
      if (result.success) {
        const backendReward = {
          name: result.item.name,
          value: result.item.value,
          rarity: result.item.rarity,
          emoji: result.item.emoji || '🎁',
          color: result.item.color || '#ffb703'
        };
        
        setFinalReward(backendReward);
        const items = generateRollItems(backendReward);
        setRollItems(items);
        
        const targetIndex = items.findIndex(item => item.id === 'target');
        const distance = calculateRollDistance(targetIndex);
        setRollDistance(distance);

        setTimeout(() => {
          setIsOpening(false);
          setIsRolling(true);
          
          if (rollingSoundRef.current) {
            rollingSoundRef.current.currentTime = 0;
            rollingSoundRef.current.play().catch(err => console.log('Could not play rolling sound:', err));
          }
          
          setTimeout(() => {
            if (rollingSoundRef.current) {
              rollingSoundRef.current.pause();
              rollingSoundRef.current.currentTime = 0;
            }
            
            if (winSoundRef.current) {
              winSoundRef.current.currentTime = 0;
              winSoundRef.current.play().catch(err => console.log('Could not play win sound:', err));
            }
            
            setIsRolling(false);
            setShowResult(true);
          }, 3000);
        }, 2000);
      }
    } catch (error) {
      console.error('Error opening case:', error);
      setIsOpening(false);
      alert('Failed to open case');
    }
  };

  const handleClose = () => {
    if (openingSoundRef.current) {
      openingSoundRef.current.pause();
      openingSoundRef.current.currentTime = 0;
    }
    if (rollingSoundRef.current) {
      rollingSoundRef.current.pause();
      rollingSoundRef.current.currentTime = 0;
    }
    if (winSoundRef.current) {
      winSoundRef.current.pause();
      winSoundRef.current.currentTime = 0;
    }
    
    setIsOpening(false);
    setIsRolling(false);
    setShowResult(false);
    setFinalReward(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="case-opening-overlay">
      <motion.div 
        className="case-opening-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="modal-header">
          <h2>Case Opening</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {!isOpening && !isRolling && !showResult && (
            <div className="case-preview-section">
              <div className="case-display">
                <img src="case.gif" alt="Case" className="case-image" />
                <h3>Open {caseType} Case</h3>
                <p>Click to open and see what's inside!</p>
                <button className="open-case-btn" onClick={handleOpenCase}>
                  🎲 Open Case
                </button>
              </div>
            </div>
          )}

          {isOpening && (
            <div className="case-opening-section">
              <h3>Opening your case...</h3>
              <div className="case-opening-animation">
                <img src="case.gif" alt="Opening Case" className="opening-case-gif" />
              </div>
            </div>
          )}

          {isRolling && finalReward && rollItems.length > 0 && (
            <div className="rolling-section">
              <h3>🎰 Rolling for your reward...</h3>
              <div className="roll-container">
                <div className="roll-window">
                  <motion.div 
                    className="roll-items"
                    animate={{ x: rollDistance }}
                    transition={{ 
                      duration: 3,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                  >
                    {rollItems.map((item, index) => (
                      <div 
                        key={`${item.id}-${index}`}
                        className="roll-item"
                        style={{ backgroundColor: item.color }}
                      >
                        <div className="item-emoji">{item.emoji}</div>
                        <div className="item-name">{item.name}</div>
                        <div className="item-value">${item.value.toFixed(2)}</div>
                        <div className="item-rarity">{item.rarity}</div>
                      </div>
                    ))}
                  </motion.div>
                  <div className="roll-pointer"></div>
                </div>
              </div>
            </div>
          )}

          <AnimatePresence>
            {showResult && finalReward && (
              <motion.div 
                className="result-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5 }}
              >
                <div className="result-card" style={{ borderColor: finalReward.color }}>
                  <div className="result-icon" style={{ backgroundColor: finalReward.color }}>
                    {finalReward.emoji}
                  </div>
                  <h3>🎉 Congratulations!</h3>
                  <div className="result-details">
                    <div className="rarity-badge" style={{ backgroundColor: finalReward.color }}>
                      {finalReward.rarity}
                    </div>
                    <div className="reward-name">{finalReward.name}</div>
                    <div className="reward-amount">
                      ${finalReward.value.toFixed(2)}
                    </div>
                  </div>
                  <button className="collect-btn" onClick={handleClose}>
                    Collect Reward
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

function Cases() {
  const { user, setUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [userCases, setUserCases] = useState({});
  const [availableCases, setAvailableCases] = useState([]);
  const [caseHistory, setCaseHistory] = useState([]);
  const [openingCase, setOpeningCase] = useState(false);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [selectedCaseType, setSelectedCaseType] = useState(null);
  const [activeSection, setActiveSection] = useState('inventory'); // 'inventory' or 'history'

  // Fetch user cases data
  const fetchUserCases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('https://test3-0ar4.onrender.com/api/user/cases', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableCases(data.availableCases);
        setUserCases(data.userCases);
      }
    } catch (error) {
      console.error('Error fetching user cases:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch case history
  const fetchCaseHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('https://test3-0ar4.onrender.com/api/user/case-history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCaseHistory(data.history);
      }
    } catch (error) {
      console.error('Error fetching case history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserCases();
    fetchCaseHistory();
  }, []);

  // Handle case opening with animation
  const handleOpenCase = async (caseType) => {
    if (openingCase) return;
    
    const caseCount = userCases[caseType] || 0;
    if (caseCount <= 0) {
      alert('You don\'t have any cases of this type!');
      return;
    }

    setSelectedCaseType(caseType);
    setShowCaseModal(true);
  };

  // API call for case opening (used by the animation component)
  const openCaseAPI = async (caseType) => {
    try {
      setOpeningCase(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`https://test3-0ar4.onrender.com/api/user/open-case/${caseType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update user balance
        setUser(prevUser => ({ ...prevUser, balance: data.newBalance }));
        
        // Update case inventory
        setUserCases(prev => ({
          ...prev,
          [caseType]: data.remainingCases
        }));
        
        // Refresh case history
        fetchCaseHistory();
        fetchUserCases();
        
        return {
          success: true,
          item: data.item,
          newBalance: data.newBalance,
          remainingCases: data.remainingCases
        };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open case');
      }
    } catch (error) {
      console.error('Error opening case:', error);
      throw error;
    } finally {
      setOpeningCase(false);
    }
  };

  // Get case display info
  const getCaseInfo = (caseId) => {
    const caseInfo = {
      common: {
        name: 'Common Case',
        color: '#10b981',
        icon: '📦',
        description: 'Basic cases with common rewards',
        minValue: '$0.10',
        maxValue: '$2.00'
      },
      rare: {
        name: 'Rare Case',
        color: '#3b82f6',
        icon: '🎁',
        description: 'Better chances for rare items',
        minValue: '$0.50',
        maxValue: '$10.00'
      },
      epic: {
        name: 'Epic Case',
        color: '#ffb703',
        icon: '💎',
        description: 'Epic rewards with high values',
        minValue: '$2.00',
        maxValue: '$50.00'
      }
    };
    
    return caseInfo[caseId] || caseInfo.common;
  };

  // Render case inventory
  const renderInventory = () => (
    <div className="cases-inventory">
      <div className="inventory-header">
        <h3>🎒 Your Case Inventory</h3>
        <p>Open cases to discover amazing rewards!</p>
      </div>
      
      <div className="cases-grid">
        {availableCases.map((caseData) => {
          const caseInfo = getCaseInfo(caseData.id);
          const userCaseCount = userCases[caseData.id] || 0;
          
          return (
            <motion.div 
              key={caseData.id}
              className="case-item-card"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div 
                className="case-header"
                style={{ 
                  background: `linear-gradient(135deg, ${caseInfo.color}20, ${caseInfo.color}40)`,
                  borderLeft: `4px solid ${caseInfo.color}`
                }}
              >
                <div className="case-icon">{caseInfo.icon}</div>
                <div className="case-info">
                  <h4>{caseInfo.name}</h4>
                  <span className="case-count">{userCaseCount} available</span>
                </div>
              </div>
              
              <div className="case-details">
                <p>{caseInfo.description}</p>
                <div className="value-range">
                  <span>Value Range: {caseInfo.minValue} - {caseInfo.maxValue}</span>
                </div>
                <div className="case-requirements">
                  <span>Min Level: {caseData.minLevel}</span>
                </div>
              </div>
              
              <div className="case-actions">
                <button 
                  className={`open-case-btn ${userCaseCount > 0 ? 'available' : 'disabled'}`}
                  onClick={() => handleOpenCase(caseData.id)}
                  disabled={userCaseCount <= 0 || openingCase}
                >
                  {openingCase ? (
                    <FaSpinner className="fa-spin" />
                  ) : userCaseCount > 0 ? (
                    <>
                      <FaBoxOpen /> Open Case
                    </>
                  ) : (
                    'No Cases'
                  )}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );

  // Render case history
  const renderHistory = () => (
    <div className="case-history">
      <div className="history-header">
        <h3>📦 Case Opening History</h3>
        <p>Your recent case opening results</p>
      </div>
      
      {caseHistory.length === 0 ? (
        <div className="empty-history">
          <FaHistory className="empty-icon" />
          <h4>No Case History</h4>
          <p>Open some cases to see your rewards here!</p>
        </div>
      ) : (
        <div className="history-list">
          {caseHistory.slice(0, 20).map((record, index) => (
            <motion.div 
              key={index}
              className="history-item"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="history-case-info">
                <span className="case-name">{record.caseName}</span>
                <span className="open-date">{new Date(record.openedAt).toLocaleDateString()}</span>
              </div>
              <div 
                className="history-item-won"
                style={{ color: record.item.color }}
              >
                <span className="item-emoji">{record.item.emoji}</span>
                <span className="item-name">{record.item.name}</span>
                <span className="item-value">${record.item.value.toFixed(2)}</span>
              </div>
              <div 
                className="rarity-badge-small"
                style={{ backgroundColor: record.item.color }}
              >
                {record.item.rarity}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );

  // Case statistics
  const calculateStats = () => {
    const totalOpened = caseHistory.length;
    const totalWon = caseHistory.reduce((sum, record) => sum + record.item.value, 0);
    const bestReward = caseHistory.reduce((best, record) => 
      record.item.value > best.value ? record : best, 
      { item: { value: 0, name: 'None' } }
    );
    
    return { totalOpened, totalWon, bestReward };
  };

  const stats = calculateStats();

  return (
    <div className="cases-container">
      <div className="cases-header">
        <h1>Case Opening</h1>
        <p>Open cases and discover amazing rewards!</p>
      </div>

      {/* Statistics Cards */}
      <div className="cases-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <FaBoxOpen />
          </div>
          <div className="stat-info">
            <h3>{stats.totalOpened}</h3>
            <p>Total Cases Opened</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaCoins />
          </div>
          <div className="stat-info">
            <h3>${stats.totalWon.toFixed(2)}</h3>
            <p>Total Won</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaStar />
          </div>
          <div className="stat-info">
            <h3>${stats.bestReward.item.value.toFixed(2)}</h3>
            <p>Best Reward</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">
            <FaGift />
          </div>
          <div className="stat-info">
            <h3>{Object.values(userCases).reduce((sum, count) => sum + count, 0)}</h3>
            <p>Cases Available</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="cases-navigation">
        <button 
          className={`nav-tab ${activeSection === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveSection('inventory')}
        >
          <FaBoxes /> Case Inventory
        </button>
        <button 
          className={`nav-tab ${activeSection === 'history' ? 'active' : ''}`}
          onClick={() => setActiveSection('history')}
        >
          <FaHistory /> Opening History
        </button>
      </div>

      {/* Content Section */}
      <div className="cases-content">
        {loading ? (
          <div className="loading-spinner">
            <FaSpinner className="fa-spin" />
            <p>Loading cases...</p>
          </div>
        ) : (
          <>
            {activeSection === 'inventory' && renderInventory()}
            {activeSection === 'history' && renderHistory()}
          </>
        )}
      </div>

      {/* How it Works Section */}
      <div className="cases-info-section">
        <h3>🎯 How Case Opening Works</h3>
        <div className="info-grid">
          <div className="info-card">
            <div className="info-icon">🎁</div>
            <h4>Earn Cases</h4>
            <p>Get cases by leveling up, completing tasks, or through special promotions</p>
          </div>
          
          <div className="info-card">
            <div className="info-icon">🎰</div>
            <h4>Open & Reveal</h4>
            <p>Open cases to reveal random rewards with different rarities and values</p>
          </div>
          
          <div className="info-card">
            <div className="info-icon">💰</div>
            <h4>Collect Rewards</h4>
            <p>All rewards are instantly added to your balance for immediate use</p>
          </div>
          
          <div className="info-card">
            <div className="info-icon">📈</div>
            <h4>Level Up</h4>
            <p>Higher player levels unlock better cases with more valuable rewards</p>
          </div>
        </div>
      </div>

      {/* Case Opening Modal */}
      <CaseOpeningAnimation
        isOpen={showCaseModal}
        onClose={() => setShowCaseModal(false)}
        caseType={selectedCaseType}
        onOpenCase={openCaseAPI}
      />
    </div>
  );
}

export default Cases;
